import * as z from 'zod';
import {
  FilterType,
  MiscellaneousField,
  ReportShapeItem,
  ShapeType,
  ShipsStructure,
} from '../../reportsTypes';
import { createDynamicShapeZodSchema } from './createDynamicShapeZodSchema';
import { createDynamicFilterZodSchema } from './createDynamicFilterZodSchema';
import { createDynamicMiscellaneousZodSchema } from './createDynamicMiscellaneousZodSchema';
import { createDynamicShipsZodSchema } from './createDynamicShipsZodSchema';

/**
 * Base schema for report cloning operations.
 * Used by both CloneReport and CreateReportTemplate (QuickCreate) components.
 */
export const reportCloneFormBaseSchema = z.object({
  report_name: z.string().optional(),
  start_datetime: z.date(),
  end_datetime: z.date(),
  email: z
    .array(z.string())
    .refine(
      (emails) => emails.every((email) => email === '' || /.+@.+\..+/.test(email)),
      { message: 'Invalid email format' }
    )
    .optional(),
  mailAction: z.string().optional(),
  countryCode: z
    .string()
    .refine((value) => value === '' || /^\+[0-9]{1,3}$/.test(value), {
      message: 'Invalid country code format',
    })
    .optional(),
  phone: z
    .string()
    .refine((value) => value === '' || /^[0-9]{1,14}$/.test(value), {
      message: 'Invalid phone number format',
    })
    .optional(),
});

/**
 * Extended base schema for creating new report templates (includes template name).
 * Used by CreateReportTemplate (QuickCreate) component.
 */
export const reportCreateFormBaseSchema = reportCloneFormBaseSchema.extend({
  report_template_name: z.string().min(1, 'Report template name is required'),
});

/**
 * Creates a dynamic form schema for report cloning operations.
 * This is the SINGLE SOURCE OF TRUTH for report form schemas.
 *
 * @param shapeItems - Shape items for the current shape type
 * @param shapeType - The current shape type (shape, multiShape, ports, worldShape)
 * @param filterType - The current filter type (normal, ship, shiplist, combined)
 * @param miscellaneousFields - Miscellaneous field definitions from report type
 * @param shipsConfig - Ships configuration from report type
 * @param includeTemplateName - Whether to include report_template_name field (for creation vs cloning)
 * @param instantShape - Whether instant shape mode is enabled
 */
export function createReportCloneFormSchema(
  shapeItems: ReportShapeItem[] | undefined,
  shapeType: ShapeType,
  filterType: FilterType,
  miscellaneousFields: Record<string, MiscellaneousField> | undefined,
  shipsConfig: ShipsStructure | null | undefined,
  includeTemplateName = false,
  instantShape = false
) {
  // Use the appropriate base schema based on whether we're creating or cloning
  const baseSchema = includeTemplateName
    ? reportCreateFormBaseSchema
    : reportCloneFormBaseSchema;

  const filterSchema = createDynamicFilterZodSchema(filterType);
  const shapeSchema = createDynamicShapeZodSchema(
    shapeItems,
    shapeType,
    instantShape
  );
  const miscellaneousSchema =
    createDynamicMiscellaneousZodSchema(miscellaneousFields);
  const shipsSchema = createDynamicShipsZodSchema(shipsConfig);

  const nestedMiscellaneousSchema = z.object({
    miscellaneous: miscellaneousSchema,
  });

  const schema = baseSchema
    .merge(filterSchema)
    .merge(shapeSchema)
    .merge(nestedMiscellaneousSchema)
    .merge(shipsSchema);

  return schema;
}

/**
 * Type definition for report clone form values.
 * This ensures type consistency across components.
 */
export type ReportCloneValues = z.infer<
  ReturnType<typeof createReportCloneFormSchema>
>;
